<template>
    <v-container fluid class="pa-3" v-if="sessionActive">

        <material-header title="Downloads" info="Reports, results and other downloadable assets"
                         help="downloads"
                         icon="mdi-cloud-download">
            <template slot="buttons">

                <!-- REFRESH -->
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn :disabled="!hasPermission(minPermission)"
                               @click="refreshClicked()"
                               v-on="on"
                               icon>
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </template>
                    <span>Reload data</span>
                </v-tooltip>
            </template>

        </material-header>

        <v-alert v-if="!hasPermission(minPermission)"
                 class="body-2"
                 type="error"
        >
            Your user account doesn't have sufficient permissions to use this feature
        </v-alert>

        <v-row v-if="hasPermission(minPermission)">

            <v-col cols="12" class="pt-0">

                <v-card flat class="elevation-0">
                    <v-card-text>

                        <v-alert v-show="alertVisible" class="body-2" type="info" dismissible icon="mdi-shield-lock" @input="alertClosed">
                            Your downloads are private and cannot be seen by other users
                        </v-alert>

                        <v-data-table
                                no-results-text="No downloads found"
                                no-data-text="No downloads found"
                                :loading="loading"
                                :headers="headers"
                                :items="items"
                                :sort-by.sync="sortBy"
                                :sort-desc.sync="sortDesc"
                                :footer-props="{itemsPerPageOptions:[10,-1]}"
                                :custom-sort="sort"
                        >

                            <template v-slot:body="{ items }">
                                <tbody>

                                <tr v-if="items.length===0">
                                    <td colspan="2">
                                        <v-alert type="info" class="mt-2 mb-2 body-2" :value="true">No downloads available</v-alert>
                                    </td>
                                </tr>

                                <tr class="cursor-pointer" v-for="item in items" :key="item.id">
                                    <!-- OWNER -->
                                    <td>
                                        <avatar :image-url="item.owner.photo_url" :person-name="item.owner.name">
                                        </avatar>
                                    </td>

                                    <!-- NAME -->
                                    <td @click="downloadButtonClicked(item)">{{ item.name }}</td>

                                    <!-- FILENAME -->
                                    <td @click="downloadButtonClicked(item)">{{ item.filename }}</td>

                                    <!-- EXPIRES -->
                                    <td>
                                        <div style="white-space: nowrap;">
                                            {{ toHumanTill(item.expires) }}
                                        </div>
                                    </td>

                                    <!-- CREATED -->
                                    <td>
                                        <v-tooltip top color="primary">
                                            <template v-slot:activator="{ on }">
                                                <div class="text-no-wrap" v-on="on">
                                                    {{ toHumanTill(item.created_at) }}
                                                </div>
                                            </template>

                                            <div class="body-2 ma-2">
                                                <v-icon class="mr-2">mdi-clock</v-icon>
                                                {{ toLocalDateTime(item.created_at) }}
                                            </div>
                                        </v-tooltip>
                                    </td>

                                    <!-- ACTIONS-->
                                    <td>
                                        <div style="white-space: nowrap;">

                                            <v-tooltip top color="primary" v-if="hasPermission('view downloads')">
                                                <template v-slot:activator="{ on }">
                                                    <v-btn
                                                            class="ma-0"
                                                            icon
                                                            v-on="on"
                                                            color="primary"
                                                            @click.stop="downloadButtonClicked(item)"
                                                    >
                                                        <v-icon light small color="white">mdi-cloud-download</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Download file</span>
                                            </v-tooltip>

                                            <v-tooltip color="primary" top v-if="hasPermission('view downloads')">
                                                <template v-slot:activator="{ on }">
                                                    <v-btn class="ma-0"
                                                           icon
                                                           text
                                                           v-on="on"
                                                           color="secondary"
                                                           @click.stop="deleteButtonClicked(item.id)"
                                                    >
                                                        <v-icon light small color="white">mdi-delete</v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Delete</span>
                                            </v-tooltip>
                                        </div>
                                    </td>

                                </tr>
                                </tbody>

                            </template>
                        </v-data-table>

                    </v-card-text>
                </v-card>

            </v-col>

        </v-row>

        <confirmation-dialog
                v-model="confirmationDialogModel"
                headline="Delete this file?"
                description="Are you sure you want to delete this file? This operation cannot be undone"
                delete
                @selected="deleteDo"
        ></confirmation-dialog>

    </v-container>
</template>

<script>

    import {timeDisplay} from '../../../components/mixins/timeDisplayMixin';
    import Json from 'vue-json-pretty';
    import Store from '../store';
    import {mapActions, mapMutations, mapState} from 'vuex';
    import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
    import MaterialHeader from '../../../components/UI/MaterialHeader';
    import Avatar from '../../../components/UI/Avatar';
    import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
    import tableSorterMixin from '../../../components/mixins/tableSorterMixin';

    const vuexModule = 'Downloads';

    export default {

        mixins: [activeSessionChecker, timeDisplay, tableSorterMixin],

        components: {
            Json,
            MaterialHeader,
            Avatar,
            ConfirmationDialog
        },

        data() {
            return {
                minPermission: 'view dashboard',
                confirmationDialogModel: false,
                deleteId: null,

                headers: [
                    {
                        text: 'OWNER',
                        value: 'owner.name',
                        sort: false,
                        width: '100px'
                    },
                    {
                        text: 'NAME',
                        value: 'owner.name',
                        sort: false,
                    },
                    {
                        text: 'FILENAME',
                        value: 'filename',
                        sort: false,
                    },
                    {
                        text: 'EXPIRES',
                        align: 'left',
                        value: 'expires_at',
                        sort: false,
                        width: '140px'
                    },
                    {
                        text: 'CREATED AT',
                        align: 'left',
                        value: 'created_at',
                        sort: false,
                        width: '140px'
                    },
                    {
                        text: 'ACTIONS',
                        align: 'left',
                        value: '',
                        sort: false,
                        width: '120px'
                    },
                ],
            }
        },
        computed: {

            ...mapState(vuexModule, [
                'items',
                'loading',
                'defaultSortBy',
                'defaultSortDesc'
            ]),

            sortBy: {
                get() {
                    return this.defaultSortBy;
                },
                set(sort) {
                    this.updateDefaultSortBy(sort);
                }
            },

            sortDesc: {
                get() {
                    return this.defaultSortDesc;
                },
                set(sort) {
                    this.updateDefaultSortDesc(sort);
                }
            },

            alertVisible() {
                return localStorage.getItem('downloadsPrivateWarning') === null;
            }
        },

        watch: {
            $route(to, from) {
                if (to.name === 'downloads') {
                    this.getDownloads(true);
                }
            }
        },

        methods: {
            ...mapActions(vuexModule, [
                'fetchDownloads',
                'downloadItem',
                'deleteItem'
            ]),

            ...mapMutations(vuexModule, [
                'updateDefaultSortBy',
                'updateDefaultSortDesc'
            ]),

            refreshClicked() {
                this.fetchDownloads(true);
            },

            getDownloads(force = false) {
                this.fetchDownloads(force)
                    .then(() => {

                    })
                    .catch(error => {
                        Bus.$emit('flash-message', {message: 'We failed to fetch your downloads!', type: 'error'});
                    });
            },

            downloadButtonClicked(item) {
                this.downloadItem(item.id)
                    .then(response => {
                        this.forceFileDownload(response, item)
                    })
                    .catch(() => {
                        Bus.$emit('flash-message', {message: 'Download failed!', type: 'error'});
                    });
            },

            forceFileDownload(response, item) {
                const url = window.URL.createObjectURL(new Blob([response.data]))
                const link = document.createElement('a')

                const fileName = response.headers['content-disposition'].replace('attachment; filename=','');

                link.href = url
                link.setAttribute('download', fileName);
                document.body.appendChild(link)
                link.click()
            },

            deleteButtonClicked(id) {

                this.confirmationDialogModel = true;
                this.deleteId = id;
            },

            deleteDo(confirmed) {
                if (confirmed) {
                    this.deleteItem(this.deleteId)
                        .then(() => {

                        })
                        .catch(error => {
                            Bus.$emit('flash-message', {message: 'We failed to fetch your downloads!', type: 'error'});
                        });
                }
            },

            alertClosed() {
                localStorage.setItem('downloadsPrivateWarning', JSON.stringify(false));
            }
        },

        created() {
            if (!this.$store.state[vuexModule]) {
                this.$store.registerModule(vuexModule, Store);
            }

            this.getDownloads();
        }
    }

</script>
